/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { removeDataPart } from '@/actions/shelf/shelf';
import { TrapFocus } from '@/components/Dialog/Modal/TrapFocus';
import { Drawer } from '@/components/Drawer/Drawer';
import { routePaths } from '@/constants/routePaths';
import { selectOrderDetailsFromState } from '@/constants/selectors';
import { useHeaderData } from '@/features/header/api/getHeader';
import { useProductList } from '@/features/shelf/context/useProductList';
import { useDispatch } from '@/hooks/redux/useDispatch';
import { type ReduxState } from '@/types';
import { useRouter } from 'next/router';
import ProgressModal from '@/components/AZCustomComponent/ProgressModal';
import dynamic from 'next/dynamic';
import { Suspense, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

const LazyMiniCartFlyoutComp = dynamic(
  () => import('@/features/miniCartFlyout').then((mod) => mod.MiniCartFlyoutComp),
  {
    loading: () => <ProgressModal noScroll />,
  }
);

const SearchResultsMiniCart = () => {
  const router = useRouter();
  const dispatch = useDispatch();
  const { data: headerData } = useHeaderData();
  const { cartSuccess } = useSelector(({ shelf }: ReduxState) => shelf);
  const orderDetailsFromState = useSelector(selectOrderDetailsFromState);
  const { data: productListData } = useProductList();
  const [isMiniCartOpen, setIsMiniCartOpen] = useState(false);
  const miniCartModalRef = useRef<HTMLDivElement>(null);

  const miniCartSuccessNote = headerData?.miniCartSuccessNote;
  const parts = productListData?.shelfParts;
  const addToCartItem = parts?.find(
    (item) =>
      String(item.skuPricingAndAvailability?.productId) === cartSuccess?.productId &&
      String(item.skuNumber) === cartSuccess.skuId
  );
  const productImage = addToCartItem?.productImageUrl ?? '';
  const productName = {
    productBrand: addToCartItem?.brand ?? '',
    productPart: addToCartItem?.name ?? '',
    quickNote: addToCartItem?.quickNote ?? '',
  };
  const productAvailabilityInfo = {
    skuPricingAndAvailability: addToCartItem?.skuPricingAndAvailability,
    dealsInfo: addToCartItem?.dealsInfo,
    aZBadgesFlagVO: addToCartItem?.aZBadgesFlagVO,
    availabilityInfoVO: addToCartItem?.availabilityInfoVO,
  };
  const productDetails = {
    productName,
    productAvailabilityInfo,
    productPartType: addToCartItem?.originalPartTypeId,
  };

  const closeMiniCartFlyout = () => {
    dispatch(removeDataPart());
    setIsMiniCartOpen(false);
  };

  const navigateToCart = () => {
    void router.push(routePaths.cart);
  };

  useEffect(() => {
    setIsMiniCartOpen(!!cartSuccess && !!miniCartSuccessNote);
  }, [cartSuccess, miniCartSuccessNote]);

  return (
    <Drawer
      data-testid="search-results-mini-cart"
      anchor="right"
      open={isMiniCartOpen}
      onClose={closeMiniCartFlyout}
      drawerRef={miniCartModalRef}
    >
      <TrapFocus open={isMiniCartOpen}>
        <div>
          <Suspense>
            <LazyMiniCartFlyoutComp
              handleClose={closeMiniCartFlyout}
              isModalOpen={isMiniCartOpen}
              image={productImage}
              successData={cartSuccess}
              productDetails={productDetails}
              miniCartData={headerData?.miniCartMap}
              closeStoreModal={closeMiniCartFlyout}
              cartUrl={navigateToCart}
              productTitle={productName.productPart}
              pageName="pdp"
              orderDetailsFromState={orderDetailsFromState}
              parentRef={miniCartModalRef}
            />
          </Suspense>
        </div>
      </TrapFocus>
    </Drawer>
  );
};

export default SearchResultsMiniCart;
